// A friend's farm, read-only: same renderer and camera as FarmScene, no actions. Your avatar
// walks in local coordinates like on your own farm; presence/chat share the host's room.
import Phaser from 'phaser';
import { SCENE, GAME_WIDTH, GAME_HEIGHT } from '../../config.js';
import { gameState } from '../../state/gameState.js';
import { visitState } from '../../state/visitState.js';
import { loadVisitView } from './VisitSceneBase.js';
import { ensureLoaded, loadCtx, prioritiseFor } from '../../ui/sceneLoading.js';
import { needsFor } from '../../assetManifest.mjs';
import { NatalkaSprite } from '../../ui/NatalkaSprite.js';
import { WalkController } from '../../ui/WalkController.js';
import { attachRemotePresence } from '../../ui/remotePresence.js';
import { attachRoomChat } from '../../ui/ChatInput.js';
import { realtime } from '../../api/realtime.js';
import { social } from '../../api/social.js';
import { fitCharacter } from '../../ui/outdoorScale.mjs';
import { OUTDOOR_CHARACTER_SCALE } from '../../ui/carCatalog.mjs';
import { ySortDepth, UI_DEPTH } from '../../ui/ySort.js';
import { homeArrival } from '../homeLayout.mjs';
import { FarmWorld } from '../../farm/FarmWorld.js';
import { attachFarmCameraControl } from '../../farm/farmCameraControl.js';
import { attachFarmBackdrop, clockInput } from '../../farm/FarmBackdrop.js';
import { cellCentre, localToCell, localToCellF, cellFToLocal, inFarm, gardenExit } from '../../farm/iso.mjs';
import { podworkoState, podworkoArrivalCell } from '../../farm/podworkoPlot.mjs';
import { backyardGateArrival } from '../backyardLayout.js';
import { iconText } from '../../ui/iconText.js';
import { gameButton } from '../../ui/gameUi.js';
import { attachVisitHud } from '../../ui/visitHud.js';
import { DOM_BUTTON } from '../../farm/FarmHud.js';
import { paintTile, TOOL_TILE } from '../../farm/FarmToolStrip.js';
import { attachVisitHelp } from '../../farm/VisitHelp.js';
import { areaCells } from '../../farm/footprint.mjs';
import { drawDrop } from '../../farm/farmFxArt.js';
import { showModal } from '../../ui/Modal.js';
import { showToast } from '../../ui/Toast.js';
import { audioEvents } from '../../audio/audioEvents.js';
import { isNight } from '../../farm/animalRoam.mjs';
import { cycleT } from '../../farm/skyClock.mjs';
import { ambienceFor } from '../../audio/eventMap.js';
import { audioManager } from '../../audio/AudioManager.js';
import { attachFarmFx } from '../../farm/FarmFxPeers.js';
import { serverNow } from '../../state/serverClock.mjs';
import { attachFarmCursor } from '../../farm/farmCursorApply.js';
import { attachFarmPopups } from '../../farm/FarmPopups.js';

const VIEWPORT = { w: GAME_WIDTH, h: GAME_HEIGHT };
const FARM_PERSPECTIVE = { refY: 0, nearY: 1, nearScale: 1, farScale: 1 };

export class VisitFarmScene extends Phaser.Scene {
  constructor() { super(SCENE.VISIT_FARM); }

  async create(data) {
    this.natalka = null;
    if (!await loadVisitView(this, data)) return;
    // The visit's copy dates from when it began: the host may have driven off, or changed their fields, since.
    await visitState.reread((id) => social.visit(id));
    const view = visitState.view;
    if (!view || !this.sys.isActive()) return;
    await ensureLoaded(this, needsFor(this.scene.key, loadCtx({ hostState: view })));
    if (!this.sys.isActive()) return;
    prioritiseFor(this.scene.key, { hostState: view });

    this.worldScale = OUTDOOR_CHARACTER_SCALE;
    this.perspective = FARM_PERSPECTIVE;
    this._exiting = false;
    this.world = new FarmWorld(this, { getData: () => visitState.view, owner: visitState.hostId });
    const size = this.world.size;
    const cell = data?.arrive === 'podworko' ? podworkoArrivalCell(size) : { x: 1, y: size - 2 };
    const c = cellCentre(cell.x, cell.y, size);
    this.natalka = new NatalkaSprite(this, c.x, c.y - 60, { worldScale: OUTDOOR_CHARACTER_SCALE, noTweakHud: true, local: true });
    this.natalka.setOutfit(gameState.raw?.inventory?.equipped);
    this.world.objects.add(this.natalka);
    this.walker = new WalkController(this, this.natalka, {
      speed: 320, xBounds: [-1e6, 1e6], floorY: -1e6, bottomY: 1e6,
      blocked: (fx, fy) => !inFarm(localToCell(fx, fy, this.world.size), this.world.size),
      onBroadcast: (p) => realtime.move(p),
      autoVector: () => this._help?.autoVector() ?? null,   // farm 5: walking up to help
    });
    fitCharacter(this);
    this._lastCell = localToCell(this.natalka.x, this.natalka.feetY, this.world.size);   // arriving on an exit is not stepping onto it
    this._cursor = attachFarmCursor(this);              // the Konewka picked: the pointer is the can (farmCursor.mjs)
    this.cam = attachFarmCameraControl(this, this.world, {
      viewport: VIEWPORT,
      getTarget: () => ({ x: this.natalka.x, y: this.natalka.feetY }),
      // Farm 5: a friend helps — an animal under the tap, else the tapped cell (farm/VisitHelp.js).
      // With the Konewka picked, a field tap waters the 2×2 block in front of it (as the 2×2 Traktor works).
      onTap: (cell, pt) => {
        const a = pt && this.world.animalLayer.animalAtScreen(pt.x, pt.y);
        if (a) this._help?.tapAnimal(a);
        else if (cell && this._tool === 'konewka') this._help?.tapArea(areaCells(cell, 2, this.world.size));
        else if (cell) this._help?.tapCell(cell);
      },
      onHover: (cell, pt) => {
        const a = pt && !pt.overHud && this.world.animalLayer.animalAtScreen(pt.x, pt.y);
        this.world.animalLayer.setHighlight(a ? a.id : null);   // which animal a click would help
        const area = !a && !pt?.overHud && cell && this._tool === 'konewka';
        this.world.setHover(a || area ? null : cell);
        this._areaCell = area ? cell : null;
        this._konewkaHover();
        this._cursor.set(area ? 'any' : null, this._tool);
      },
    });
    this.cam.snapToTarget();
    this.events.on('helped', () => this._konewkaHover());   // a pour landed: the block under the pointer changed
    this.sky = attachFarmBackdrop(this, this.world, this.cam);
    // Farm 6: the day / night ambience under the music; the 1 s tick switches it at dusk / dawn and
    // retries a clip that was still loading. Stopped when the scene goes (home, a visit, logout).
    const ambience = () => audioManager.playAmbience(ambienceFor(isNight(cycleT(clockInput()))));
    ambience();
    this._ambienceTimer = this.time.addEvent({ delay: 1000, loop: true, callback: ambience });
    this.events.once('shutdown', () => { this._ambienceTimer?.remove(); audioManager.stopAmbience(); });

    this._drawHud();

    const room = `house:${visitState.hostId}:FarmScene`;
    this._presence = attachRemotePresence(this, room);
    attachRoomChat(this, room);
    attachFarmFx(this, room, { getAnimals: () => visitState.view?.animals ?? [] });   // the host's (and other visitors') work, played here
    // 4d: the server's herd for this farm — every viewer plays the same intentions.
    const herdOffs = [
      realtime.on('herd_snapshot', (m) => m.room === room && this.world.animalLayer.applyHerd(m)),
      realtime.on('herd', (m) => m.room === room && this.world.animalLayer.applyHerd(m)),
      realtime.on('herd_sound', (m) => m.room === room && this.world.animalLayer.playSound(m)),
      realtime.on('farm_changed', (m) => m.room === room && this._refetchView()),
    ];
    this.events.once('shutdown', () => herdOffs.forEach((off) => off?.()));
    const tick = this.time.addEvent({ delay: 1000, loop: true, callback: () => this.world.refresh(serverNow()) });
    this.events.once('shutdown', () => tick.remove());
    this.popups = attachFarmPopups(this, { ownFarm: false });   // a level-up from helping (VisitHelp)
    this._help = attachVisitHelp(this, { counterPos: { x: GAME_WIDTH / 2, y: 78 } });   // farm 5: 'Pomoc: n/5'
    this.events.once('shutdown', () => { this._help?.destroy(); this._help = null; });
  }

  // The farm's own HUD, as far as a visitor can use it (owner 2026-10-27: "it should all keep its style"):
  // the visitor's HUD (ui/visitHud.js) with the host's level in the badge — no XP bar — the "Dom" button,
  // and the visitor's tools in the strip's first slots: Ręka, and the Konewka (2×2 watering, owner 2026-10-27).
  _drawHud() {
    this._level = attachVisitHud(this, '');
    this._refreshLevel();
    gameButton(this, DOM_BUTTON.x, DOM_BUTTON.y, DOM_BUTTON.w, DOM_BUTTON.h, '{house} Dom', () => this._exit('home'),
      { variant: 'wood', fontSize: 17 }).setScrollFactor(0).setDepth(UI_DEPTH);
    this._tool = 'hand';
    this._tiles = { hand: this._toolTile(0, 'hand'), konewka: this._toolTile(1, 'konewka') };
    this._refreshTools();
    const unsub = gameState.on(() => this._refreshTools());           // the Konewka bought (here or in the Rynek)
    this.events.once('shutdown', () => unsub?.());
  }

  // The Konewka's 2×2 block under the pointer: which of its fields a click would water, and how many
  // (owner 2026-10-27: "I should see if and how many things I'll water").
  _konewkaHover() {
    if (!this._areaCell) { this.world.setAreaHover(null); return; }
    const cells = areaCells(this._areaCell, 2, this.world.size);
    const pv = this._help?.previewArea(cells);
    this.world.setAreaHover(cells, pv?.hint ?? null, pv?.cells ?? null);
  }

  _toolTile(i, id) {
    const fix = (o, d = 0) => o.setScrollFactor(0).setDepth(UI_DEPTH + d);
    const { x, w, h } = TOOL_TILE, y = TOOL_TILE.y(i);
    const tile = fix(this.add.graphics());
    // a tap on it stays off the field below, and picks the tool
    fix(this.add.rectangle(x, y, w, h, 0xffffff, 0.001).setInteractive({ useHandCursor: true }))
      .on('pointerup', () => this._pickTool(id));
    if (id === 'hand') fix(iconText(this, x, y - 10, '{hand}', { fontSize: '30px' }).setOrigin(0.5), 1);
    else if (this.textures.exists('farm_tool_konewka')) {   // the Konewka's picture, as the farm's strip draws its machines
      const src = this.textures.get('farm_tool_konewka').getSourceImage();
      fix(this.add.image(x, y - 10, 'farm_tool_konewka').setDisplaySize(38, 38 * src.height / src.width), 1);   // owner: smaller
    } else fix(drawDrop(this.add.graphics().setPosition(x, y - 8), 1.6), 1);
    const label = fix(iconText(this, x, y + 24, id === 'hand' ? 'Ręka' : '', { fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '14px', color: '#fff8e8', fontStyle: 'bold', stroke: '#5a3414', strokeThickness: 3 }).setOrigin(0.5), 1);
    return { tile, label, x, y, w, h };
  }

  _refreshTools() {
    if (!this._tiles) return;
    const owned = !!gameState.raw?.farm?.konewka;
    if (!owned && this._tool === 'konewka') this._tool = 'hand';
    for (const [id, t] of Object.entries(this._tiles)) {
      paintTile(t.tile, t.x, t.y, t.w, t.h, { active: this._tool === id, enabled: true });
    }
    this._tiles.konewka.label.setText(owned ? 'Konewka 2×2' : '{cart} Konewka');
  }

  async _pickTool(id) {
    if (id === 'konewka' && !gameState.raw?.farm?.konewka) {
      if (this._buying) return;                      // the modal's overlay also eats a double tap
      this._buying = true;
      const price = gameState.raw?.catalog?.farm?.konewka?.price ?? 200;
      const ok = await showModal(this, { title: 'Kupić Konewkę?', body: `za ${price} {coin}`, confirmLabel: 'Kup' });
      try {
        if (!ok) return;
        const res = await gameState.farmActions([{ op: 'buy_konewka' }]);
        const why = res?.rejected?.[0]?.reason;
        if (why) { showToast(this, why === 'INSUFFICIENT_MONEY_FARM' ? 'Za mało pieniędzy' : 'Nie udało się', 1500); return; }
        audioEvents.emit('upgrade');
      } catch { showToast(this, 'Nie udało się', 1500); return; } finally { this._buying = false; }
    }
    if (id === 'konewka' && !gameState.raw?.farm?.konewka) return;
    this._tool = id;
    this._cursor.set(null);                          // the next hover over the field picks it up again
    this._areaCell = null;
    this.world.setAreaHover(null);
    this._refreshTools();
  }

  _refreshLevel() {
    this._level?.setText(`{xp_star} ${visitState.hostName} · Poziom ${visitState.view?.level ?? 1}`);
  }

  // The host changed their farm (a hut sold, animals bought, a field, a grow): our ground comes from
  // /visit, so fetch it again. On a grow keep our feet on the same patch (every y moved down).
  async _refetchView() {
    const hostId = visitState.hostId;
    let view;
    try { view = await social.visit(hostId); } catch { return; }
    if (!this.sys.isActive() || !this.world || visitState.hostId !== hostId) return;
    const before = this.world.size;
    const feet = localToCellF(this.natalka.x, this.natalka.feetY, before);
    visitState.view = view;
    this._help?.refreshCounter();
    this._refreshLevel();
    this.world.refresh(serverNow());
    const grew = this.world.size - before;
    if (grew) {
      const q = cellFToLocal(feet.x, feet.y + grew, this.world.size);
      this.natalka.x = q.x;
      this.natalka.y = q.y - (this.natalka.feetY - this.natalka.y);
      this.cam.snapToTarget();
    }
  }

  update(_t, dt) {
    if (!this.natalka) return;
    this.world.update(dt);
    this._help?.update(dt);
    this.walker.update(dt);
    fitCharacter(this);
    this.natalka.setDepth(this.world.animalLayer.characterDepth(this.natalka.x, this.natalka.feetY));   // beside a building: the right side of it
    for (const a of this._presence?.avatars.values() ?? []) {
      // A peer is more than its sprite: the name tag, bike, car and chat bubble are all
      // separate scene-root objects at the same local coords — left outside the panned/
      // scaled world container they'd drift by the pan offset. Re-parent whichever exist.
      for (const obj of [a.sprite, a.label, a.bike, a.car, a._bubble?.label]) {
        if (obj && obj.parentContainer !== this.world.objects) {
          const depth = obj.depth;
          this.world.objects.add(obj);
          obj.setDepth(depth);   // keep sorting above bodies (label/bubble depths exceed any feetY)
        }
      }
      a.sprite?.setDepth(this.world.animalLayer.characterDepth(a.sprite.x, a.sprite.feetY ?? a.sprite.y));
    }
    this.world.sortObjects();
    this.cam.update(dt);
    this.sky?.update();
    const cell = localToCell(this.natalka.x, this.natalka.feetY, this.world.size);
    const exit = gardenExit(cell, this.world.size,
      { podworko: !!podworkoState(visitState.view), manual: this.walker.manualMoving, from: this._lastCell });   // helping walks you; it never takes you out
    this._lastCell = cell;
    if (exit) this._exit(exit);
  }

  _exit(where) {
    if (this._exiting) return;
    this._exiting = true;
    if (where === 'podworko') this.scene.start(SCENE.VISIT_BACKYARD, backyardGateArrival());
    else this.scene.start(SCENE.VISIT_HOME, homeArrival('garden'));   // the house corner, or the "Dom" button
  }
}
